import React, { useState, useEffect } from "react";
import { Database, RefreshCw, AlertCircle, Info, Activity, Server, Layers } from "lucide-react";
import { fetchDatabaseTelemetry, DatabaseTelemetryResponse } from "./settingsApi";

export const DatabaseManagementPage: React.FC = () => {
  const [telemetry, setTelemetry] = useState<DatabaseTelemetryResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    loadTelemetry(true);
  }, []);

  const loadTelemetry = async (isInitial = false) => {
    if (isInitial) setLoading(true);
    else setRefreshing(true);
    setError("");

    try {
      const data = await fetchDatabaseTelemetry();
      setTelemetry(data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to load database telemetry.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12 text-slate-400 text-xs">
        <RefreshCw className="w-5 h-5 animate-spin mr-2 text-indigo-400" />
        <span>Querying PostgreSQL system catalogs & connection pool telemetry...</span>
      </div>
    );
  }

  const conn = telemetry?.connection;
  const metrics = telemetry?.metrics;
  const pool = telemetry?.connection_pool;
  const tables = telemetry?.tables || [];

  const connPct = pool?.max_connections
    ? Math.min(100, Math.round(((pool.active || 1) / pool.max_connections) * 100))
    : 0;

  return (
    <div className="space-y-4 text-xs font-sans">
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-950/60 border border-red-800 text-red-300 rounded-lg text-xs">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-slate-900/80 border border-slate-800 rounded-xl">
        <div className="flex items-center gap-2">
          <Database className="w-5 h-5 text-indigo-400" />
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-bold text-slate-100">PostgreSQL Database Management</h2>
              <span className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold ${
                conn?.connected
                  ? "bg-emerald-950 text-emerald-400 border border-emerald-800"
                  : "bg-rose-950 text-rose-400 border border-rose-800"
              }`}>
                {conn?.status || "Unavailable"}
              </span>
            </div>
            <p className="text-[10px] text-slate-500">
              Database: <code className="text-indigo-300 font-mono">{conn?.database_name || "default"}</code> · Tenant Schema: <code className="text-amber-400 font-mono">{telemetry?.tenant_schema || "public"}</code>
            </p>
          </div>
        </div>

        <button
          onClick={() => loadTelemetry(false)}
          disabled={refreshing}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-200 rounded-lg transition-colors text-[11px]"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin text-indigo-400" : ""}`} />
          <span>{refreshing ? "Refreshing..." : "Refresh Telemetry"}</span>
        </button>
      </div>

      {/* DB Overview Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "PostgreSQL Engine",   value: conn?.version || "Unavailable", color: "text-indigo-400" },
          { label: "Est. Total Rows",     value: metrics?.total_rows_estimate.toLocaleString() || "0", color: "text-slate-100" },
          { label: "Database Disk Size",  value: metrics?.formatted_db_size || "Unavailable", color: "text-amber-400" },
          { label: "Active Connections", value: `${pool?.active || 0} / ${pool?.max_connections || 100}`, color: "text-emerald-400" },
        ].map((m) => (
          <div key={m.label} className="p-3 bg-slate-950 border border-slate-800 rounded-xl text-center">
            <p className={`text-base font-bold font-mono ${m.color}`}>{m.value}</p>
            <p className="text-[10px] text-slate-500 mt-0.5">{m.label}</p>
          </div>
        ))}
      </div>

      {/* Connection Pool */}
      <div className="p-4 bg-slate-900/80 border border-slate-800 rounded-xl space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-[11px] font-bold font-mono text-slate-400 uppercase flex items-center gap-1.5 font-sans">
            <Activity className="w-3.5 h-3.5 text-emerald-400" />
            Connection Pool & Server Sessions (pg_stat_activity)
          </h3>
          <span className="text-[10px] text-slate-500 font-mono">Max Server Limit: {pool?.max_connections || 100}</span>
        </div>
        <div className="flex items-center justify-between text-[11px] font-mono">
          <span className="text-slate-400">Active: <span className="text-emerald-400 font-bold">{pool?.active || 0}</span></span>
          <span className="text-slate-400">Idle: <span className="text-slate-300 font-bold">{pool?.idle || 0}</span></span>
          <span className="text-slate-400">Waiting: <span className="text-amber-400 font-bold">{pool?.waiting || 0}</span></span>
          <span className="text-slate-400">Total Connection Count: <span className="text-indigo-400 font-bold">{pool?.total || 0}</span></span>
        </div>
        <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
          <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${Math.max(5, connPct)}%` }} />
        </div>
      </div>

      {/* Migration & Schema Telemetry */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="p-3.5 bg-slate-950 border border-slate-800 rounded-xl flex items-center justify-between">
          <div>
            <span className="text-[10px] text-slate-500 block font-sans">Django Applied Migrations</span>
            <span className="text-sm font-bold font-mono text-slate-200">{metrics?.applied_migrations || 0} Migrations Applied</span>
          </div>
          <Layers className="w-5 h-5 text-indigo-400" />
        </div>

        <div className="p-3.5 bg-slate-950 border border-slate-800 rounded-xl flex items-center justify-between">
          <div>
            <span className="text-[10px] text-slate-500 block font-sans">Latest Applied Migration</span>
            <span className="text-xs font-bold font-mono text-amber-400">
              {metrics?.latest_migration_applied ? new Date(metrics.latest_migration_applied).toLocaleDateString() : "Up to date"}
            </span>
          </div>
          <Server className="w-5 h-5 text-amber-400" />
        </div>
      </div>

      {/* Real Table Statistics */}
      <div className="p-4 bg-slate-900/80 border border-slate-800 rounded-xl space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-[11px] font-bold font-mono text-slate-400 uppercase font-sans">
            Tenant Table Statistics ({tables.length} Tables Scoped to <code className="text-amber-400 font-mono">{telemetry?.tenant_schema}</code>)
          </h3>
          <span className="text-[10px] text-slate-500 font-mono">Sorted by Disk Size</span>
        </div>

        {tables.length === 0 ? (
          <p className="text-slate-500 text-center py-4 font-mono text-[11px]">No tables indexed for this schema.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[10px] font-mono">
              <thead>
                <tr className="text-slate-500 border-b border-slate-800">
                  <th className="text-left py-2 pr-4 font-sans">Table Name</th>
                  <th className="text-right py-2 pr-4 font-sans">Est. Rows</th>
                  <th className="text-right py-2 pr-4 font-sans">Total Size</th>
                  <th className="text-right py-2 pr-4 font-sans">Indexes</th>
                  <th className="text-right py-2 font-sans">Last Vacuum / Analyze</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-900">
                {tables.map((t) => (
                  <tr key={t.name} className="hover:bg-slate-900/50 transition-colors">
                    <td className="py-2 pr-4 text-indigo-300 font-bold">{t.name}</td>
                    <td className="py-2 pr-4 text-right text-slate-300">{t.rows.toLocaleString()}</td>
                    <td className="py-2 pr-4 text-right text-slate-400">{t.formatted_size}</td>
                    <td className="py-2 pr-4 text-right text-slate-400">{t.indexes}</td>
                    <td className="py-2 text-right text-slate-500">{t.last_vacuum}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Limitations Callout */}
      <div className="p-3 bg-slate-950 border border-slate-800 rounded-xl flex items-start gap-2 text-[11px] text-slate-400">
        <Info className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
        <div>
          <span className="font-bold text-slate-300 block mb-0.5 font-sans">Telemetry Scope & Limitations</span>
          <p className="text-[10px] text-slate-500 font-sans">
            Destructive mutation commands (VACUUM, REINDEX, TRUNCATE) are disabled in read-only telemetry mode. PgBouncer internal proxy stats are <strong className="text-slate-400 font-mono">Unavailable</strong> via standard application connection.
          </p>
        </div>
      </div>
    </div>
  );
};
